import React, { useState, useEffect } from 'react';
import TenantLayout from '../components/TenantLayout';
import { useAuth } from '../context/AuthContext';
import { useLanguage } from '../context/LanguageContext';
import { useParams, Link } from 'react-router-dom';
import { TrendingUp, TrendingDown, ArrowLeftRight, FileSpreadsheet } from 'lucide-react';
import { PieChart, Pie, Cell, Tooltip, ResponsiveContainer, BarChart, Bar, XAxis, YAxis, CartesianGrid, Legend, LineChart, Line, AreaChart, Area } from 'recharts';

export default function TenantDashboard() {
    const { clientSlug } = useParams();
    const { api } = useAuth();
    const { t } = useLanguage();
    const [stats, setStats] = useState({
        total_income: 0,
        total_expense: 0,
        payment_received: 0,
        payment_going: 0,
        net_balance: 0,
        tx_stats: [],
        pay_stats: [],
        monthly_trend: []
    });
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        const fetchStats = async () => {
            try {
                const res = await api.get(`/api/tenant/${clientSlug}/dashboard`);
                setStats(res.data);
            } catch (err) {
                console.error("Failed to load dashboard stats", err);
            } finally {
                setLoading(false);
            }
        };
        fetchStats();
    }, [clientSlug, api]);

    const formatCurrency = (val) => {
        return new Intl.NumberFormat('en-IN', { style: 'currency', currency: 'INR', maximumFractionDigits: 0 }).format(val || 0);
    };

    const formatMonth = (yyyy_mm) => {
        if (!yyyy_mm) return '';
        const [year, month] = yyyy_mm.split('-');
        const date = new Date(year, parseInt(month) - 1, 1);
        return date.toLocaleDateString('en-IN', { month: 'short', year: '2-digit' });
    };

    const desks = [
        {
            id: "desk-income-card",
            title: t("Income / Sales"),
            route: `/${clientSlug}/income`,
            icon: TrendingUp,
            tagline: t("Record sales invoices & incoming funds"),
            accent: "border-l-emerald-500 hover:border-l-amber-500",
            statLabel: t("Total Billed & Settled In"),
            statValue: stats.total_income,
            statColor: "text-emerald-600"
        },
        {
            id: "desk-expense-card",
            title: t("Expense / Purchase"),
            route: `/${clientSlug}/expense`,
            icon: TrendingDown,
            tagline: t("Track vendor bills & purchases"),
            accent: "border-l-rose-500 hover:border-l-amber-500",
            statLabel: t("Total Billed & Settled Out"),
            statValue: stats.total_expense,
            statColor: "text-rose-600"
        },
        {
            id: "desk-payment-card",
            title: t("Payment Master"),
            route: `/${clientSlug}/payments`,
            icon: ArrowLeftRight,
            tagline: t("Two-way settlement ledger"),
            accent: "border-l-indigo-500 hover:border-l-amber-500",
            statLabel: t("Net Cashflow"),
            statValue: stats.payment_received - stats.payment_going,
            statColor: stats.payment_received - stats.payment_going >= 0 ? "text-indigo-600" : "text-rose-600"
        },
        {
            id: "desk-reports-card",
            title: t("Reports Desk"),
            route: `/${clientSlug}/reports`,
            icon: FileSpreadsheet,
            tagline: t("Dynamic ledger history & export"),
            accent: "border-l-amber-500 bg-amber-50/40 hover:bg-amber-50/80",
            statLabel: t("P&L Balance"),
            statValue: stats.net_balance,
            statColor: "text-[#0F284E]"
        }
    ];

    // Chart Data Preparation
    const pnlData = [
        { name: t('Billed & Settled In'), value: stats.total_income, color: '#10B981' }, // emerald-500
        { name: t('Billed & Settled Out'), value: stats.total_expense, color: '#F43F5E' }  // rose-500
    ];

    const cashflowData = [
        { name: t('Cashflow (Received vs Going)'), Received: stats.payment_received, Going: stats.payment_going }
    ];

    const trendData = (stats.monthly_trend || []).map(m => ({
        month: formatMonth(m.month),
        Income: m.income,
        Expense: m.expense,
        Profit: m.income - m.expense
    }));

    const CustomTooltip = ({ active, payload }) => {
        if (active && payload && payload.length) {
            return (
                <div className="bg-white p-3 border border-slate-200 shadow-lg rounded-xl text-sm z-50">
                    <p className="font-semibold text-slate-800 mb-2 border-b border-slate-100 pb-1">{payload[0].payload.name || payload[0].payload.month || 'Value'}</p>
                    {payload.map((entry, index) => (
                        <div key={index} className="flex items-center gap-2 mb-1">
                            <div className="w-2 h-2 rounded-full" style={{backgroundColor: entry.color}}></div>
                            <span className="text-slate-500 text-xs">{entry.name}:</span>
                            <span className="font-mono font-bold text-slate-800 ml-auto">{formatCurrency(entry.value)}</span>
                        </div>
                    ))}
                </div>
            );
        }
        return null;
    };

    return (
        <TenantLayout title={t("Dashboard Overview")} tagline={t("Financial summary for the current period.")}>
            
            {/* Dashboard Desks - Full Width */}
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 mb-4 w-full">
                {desks.map((desk) => (
                    <Link 
                        key={desk.id} 
                        to={desk.route}
                        data-testid={desk.id}
                        className={`relative bg-white rounded-xl border border-slate-200 border-t-4 ${desk.accent.replace('border-l-4 border-l', 'border-t')} p-4 shadow-sm hover:shadow-md transition-all duration-300 overflow-hidden group cursor-pointer flex flex-col hover:-translate-y-1`}
                    >
                        <div className="flex items-center justify-between mb-2">
                             <div className="w-8 h-8 shrink-0 rounded-lg flex items-center justify-center bg-[#0A192F] text-[#EAB308] group-hover:scale-110 transition-transform">
                                 <desk.icon size={16} />
                             </div>
                        </div>

                        <div>
                            <h3 className="text-sm sm:text-base font-bold text-[#0F284E] font-['Cabinet_Grotesk'] leading-tight mb-1">{desk.title}</h3>
                        </div>

                        <div className="mt-auto pt-2 border-t border-slate-100 flex flex-col gap-1">
                            <span className="text-[10px] text-slate-500 uppercase font-semibold">{desk.statLabel}</span>
                            <div className={`font-mono font-bold text-sm sm:text-base ${desk.statColor}`}>
                                {loading ? '...' : formatCurrency(desk.statValue)}
                            </div>
                        </div>
                    </Link>
                ))}
            </div>

            {/* Top Level Summary Cards & Charts */}
            <div className="grid grid-cols-1 lg:grid-cols-4 gap-4 mb-4">
                 <div className="lg:col-span-1 bg-white rounded-xl p-4 border border-slate-200 shadow-sm flex flex-col justify-center">
                     <div className="text-xs font-medium text-slate-500 mb-1">{t("Net Income vs Expense")}</div>
                     <div className={`font-mono font-bold text-3xl tracking-tight mb-2 ${stats.net_balance >= 0 ? 'text-emerald-600' : 'text-rose-600'}`}>
                         {formatCurrency(stats.net_balance)}
                     </div>
                     <div className="text-[11px] text-slate-400 border-t border-slate-100 pt-2 leading-tight">
                         {t("P&L balance based on recorded invoices and bills.")}
                     </div>
                 </div>

                 <div className="lg:col-span-1 bg-white rounded-xl p-3 border border-slate-200 shadow-sm h-48 flex flex-col">
                     <h3 className="text-xs font-bold text-[#0F284E] mb-1 text-center">{t("Income vs Expense")}</h3>
                     <div className="flex-1 w-full min-h-0">
                         <ResponsiveContainer width="100%" height="100%">
                            <PieChart>
                                <Pie
                                    data={pnlData}
                                    cx="50%"
                                    cy="50%"
                                    innerRadius={40}
                                    outerRadius={65}
                                    paddingAngle={5}
                                    dataKey="value"
                                >
                                    {pnlData.map((entry, index) => (
                                        <Cell key={`cell-${index}`} fill={entry.color} />
                                    ))}
                                </Pie>
                                <Tooltip content={<CustomTooltip />} />
                                <Legend verticalAlign="bottom" height={24} iconType="circle" wrapperStyle={{fontSize: '11px', bottom: 0}}/>
                            </PieChart>
                        </ResponsiveContainer>
                     </div>
                 </div>

                 <div className="lg:col-span-2 bg-white rounded-xl p-3 border border-slate-200 shadow-sm h-48 flex flex-col">
                     <h3 className="text-xs font-bold text-[#0F284E] mb-2 pl-2">{t("6-Month Trend")}</h3>
                     <div className="flex-1 w-full min-h-0">
                         {trendData.length > 0 ? (
                             <ResponsiveContainer width="100%" height="100%">
                                <AreaChart data={trendData} margin={{ top: 5, right: 10, left: -20, bottom: 0 }}>
                                    <defs>
                                        <linearGradient id="colorIncome" x1="0" y1="0" x2="0" y2="1">
                                            <stop offset="5%" stopColor="#10B981" stopOpacity={0.3}/>
                                            <stop offset="95%" stopColor="#10B981" stopOpacity={0}/>
                                        </linearGradient>
                                        <linearGradient id="colorExpense" x1="0" y1="0" x2="0" y2="1">
                                            <stop offset="5%" stopColor="#F43F5E" stopOpacity={0.3}/>
                                            <stop offset="95%" stopColor="#F43F5E" stopOpacity={0}/>
                                        </linearGradient>
                                    </defs>
                                    <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#f1f5f9" />
                                    <XAxis dataKey="month" axisLine={false} tickLine={false} tick={{fontSize: 10, fill: '#64748b'}} dy={5} />
                                    <YAxis axisLine={false} tickLine={false} tickFormatter={(val) => `₹${val >= 1000 ? (val/1000).toFixed(0)+'k' : val}`} fontSize={10} stroke="#94a3b8" />
                                    <Tooltip content={<CustomTooltip />} />
                                    <Legend verticalAlign="top" align="right" height={24} iconType="circle" wrapperStyle={{fontSize: '11px', top: -10}}/>
                                    <Area type="monotone" dataKey="Income" stroke="#10B981" strokeWidth={2} fillOpacity={1} fill="url(#colorIncome)" />
                                    <Area type="monotone" dataKey="Expense" stroke="#F43F5E" strokeWidth={2} fillOpacity={1} fill="url(#colorExpense)" />
                                </AreaChart>
                            </ResponsiveContainer>
                         ) : (
                             <div className="w-full h-full flex items-center justify-center text-slate-400 text-xs">
                                 {t("Not enough data for trend analysis")}
                             </div>
                         )}
                     </div>
                 </div>
            </div>

             <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
                 <div className="bg-white rounded-xl p-4 border border-slate-200 shadow-sm h-48 flex flex-col">
                     <h3 className="text-xs font-bold text-[#0F284E] mb-2 text-center">{t("Cashflow Master (Received vs Going)")}</h3>
                     <div className="flex-1 w-full min-h-0">
                         <ResponsiveContainer width="100%" height="100%">
                            <BarChart data={cashflowData} margin={{ top: 5, right: 10, left: -20, bottom: 0 }}>
                                <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#E2E8F0" />
                                <XAxis dataKey="name" axisLine={false} tickLine={false} tick={false} />
                                <YAxis axisLine={false} tickLine={false} tickFormatter={(val) => `₹${val >= 1000 ? (val/1000).toFixed(0)+'k' : val}`} fontSize={10} stroke="#64748B" />
                                <Tooltip cursor={{fill: '#f8fafc'}} content={<CustomTooltip />} />
                                <Legend verticalAlign="bottom" height={24} iconType="circle" wrapperStyle={{fontSize: '11px', paddingTop: '5px'}}/>
                                <Bar dataKey="Received" fill="#6366F1" radius={[4, 4, 0, 0]} maxBarSize={50} />
                                <Bar dataKey="Going" fill="#F43F5E" radius={[4, 4, 0, 0]} maxBarSize={50} />
                            </BarChart>
                        </ResponsiveContainer>
                     </div>
                 </div>
                 
                 <div className="bg-gradient-to-br from-[#0A192F] to-[#163868] rounded-xl p-5 border border-[#1D4B88] shadow-sm flex flex-col justify-center text-white relative overflow-hidden h-48">
                     <div className="absolute top-0 right-0 w-32 h-32 bg-white/5 rounded-full blur-3xl -mr-10 -mt-10"></div>
                     <div className="absolute bottom-0 left-0 w-24 h-24 bg-[#EAB308]/10 rounded-full blur-2xl -ml-10 -mb-10"></div>
                     
                     <div className="relative z-10">
                         <div className="flex items-center gap-2 mb-2 text-[#EAB308]">
                             <FileSpreadsheet size={20} />
                             <h3 className="font-bold text-base font-['Cabinet_Grotesk']">{t("Quick Reports")}</h3>
                         </div>
                         <p className="text-slate-300 text-xs mb-4 leading-relaxed max-w-sm">
                             {t("View your complete ledger, filter by parties, export to PDF or Excel instantly. Stay on top of your reconciliations.")}
                         </p>
                         <Link 
                             to={`/${clientSlug}/reports`}
                             className="inline-flex items-center gap-2 bg-[#EAB308] hover:bg-amber-400 text-[#0A192F] font-bold px-4 py-2 rounded-lg transition-colors text-xs shadow-sm hover:shadow-md w-max"
                         >
                             {t("Go to Reports Desk")} →
                         </Link>
                     </div>
                 </div>
             </div>
        </TenantLayout>
    );
}
